import {
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { AdobeLaunchService } from '../../../../services/adobe-launch/adobe-launch.service';
import { ContentUtil } from '../../../../utils/content/content.util';
import {
    CheckboxComponent,
    CheckboxInputComponent,
    CheckboxState,
} from '@vg-constellation/angular-20/checkbox';
import { LinkComponent } from '@vg-constellation/angular-20/link';
import { IconComponent } from '@vg-constellation/angular-20/icon';
import { BannerComponent } from '@vg-constellation/angular-20/banner';
import { ModalDialogComponent } from '@vg-constellation/angular-20/modal-dialog';
import { BehaviorSubject, combineLatest, Observable, Subscription, take } from 'rxjs';
import {
    AccountWithPendingInfo,
    CheckPendingRequestsDto,
    CheckPendingRequestsResponseDto,
    MultiAccountItemDto,
} from '../../../../../common/dtos/check-pending-requests.dto';
import { PendingRequestService } from '../../../../services/pending-request/pending-request.service';
import { OwnAccountDTO } from '../../../../../common/dtos/own-account.dto';
import { IneligibleReasons } from '../../../../../common/enums/ineligible-scenarios.enum';
import { AccessTypeSelection } from '../../../../../common/enums/access-type-selection.enum';
import { selectOwnAccounts, selectUserInfo } from '../../../../store/selectors/account-permissions.selector';
import {
    selectAddUserStepperSelectedAccess,
    selectAddUserStepperSelectedAccessLevelLabel,
    selectAddUserStepperSelectedAccounts,
    selectAddUserStepperSelectedUser,
    selectAddUserStepperSelectedUserName,
} from '../../../../store/selectors/grant-revoke-permissions.selector';
import { updateAddUserStepperSelectedAccounts } from '../../../../store/actions/add-user-stepper-flow.action';
import { ErrorService } from '../../../../services/generic-error/error.service';
import { LoggerService } from '../../../../services/logger/logger.service';
import { ErrorPage } from '../../../../services/generic-error/error-page-utils';
import { environment } from '../../../../../environments/environment';
import { LinkTarget } from '../../../../enums/link-target.enum';
import { ReplaceParamPipe } from '../../../../pipes/replace-param/replace-param.pipe';
import accountPermissionUtil from '../../../../utils/account-permission/account-permission.util';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { adobeConfigData } from './gaf-accounts-adobe';
import { SkeletonLoaderComponent } from '@vg-constellation/angular-20/skeleton-loader';

interface AccountGroup {
    eligible: OwnAccountDTO[];
    pendingRequests: AccountWithPendingInfo[];
    requireForm: OwnAccountDTO[];
    otherIneligible: OwnAccountDTO[];
    temporarilyIneligible: OwnAccountDTO[];
}

@AdobeTagging(adobeConfigData)
@Component({
    selector: 'zci-gaf-accounts',
    standalone: true,
    imports: [
        CommonModule,
        CheckboxComponent,
        CheckboxInputComponent,
        LinkComponent,
        IconComponent,
        BannerComponent,
        ModalDialogComponent,
        ReplaceParamPipe,
        SkeletonLoaderComponent,
    ],
    templateUrl: './gaf-accounts.component.html',
    styleUrls: ['./gaf-accounts.component.scss'],
})
export class GafAccountsComponent implements OnInit, OnDestroy {
    @ViewChild('selectionError') selectionErrorElement?: ElementRef<HTMLDivElement>;
    @ViewChild('managedAccountModal', { static: false }) managedAccountModal!: ModalDialogComponent;
    @ViewChild('cashManagementModal', { static: false }) cashManagementModal!: ModalDialogComponent;
    @Input() nextStepReady: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() locationChanged: EventEmitter<any> = new EventEmitter<any>();
    grantorContent = ContentUtil.grantorFragmentContent;
    granteeName$: Observable<string>;
    accessLevelLabel$: Observable<string>;
    selectedAccess$: Observable<AccessTypeSelection>;
    AccessTypeSelection = AccessTypeSelection;
    selectedAccountIds: Set<string> = new Set();
    eligibleAccountCount = 0;
    showSelectionError = false;
    isLoading = true;
    pendingRequestsResponse: CheckPendingRequestsResponseDto | null = null;
    protected adobeLaunchService = inject(AdobeLaunchService);
    private readonly store = inject(Store);
    private readonly pendingRequestService = inject(PendingRequestService);
    private readonly errorService = inject(ErrorService);
    private readonly loggerService = inject(LoggerService);
    private readonly accessLevelLabels: Record<AccessTypeSelection, string> = {
        [AccessTypeSelection.LIMITED_ACCESS]: 'Limited authority',
        [AccessTypeSelection.INFORMATION_ONLY]: 'Information only',
        [AccessTypeSelection.FULL_AUTHORITY]: 'Full authority',
    };
    private readonly accountLevelIneligibleReasonLabels: Record<number, string> = {
        [IneligibleReasons.ZERO_BALANCE]: 'Zero balance',
        [IneligibleReasons.ADDRESS_HOLD]: 'Recent address change',
        [IneligibleReasons.PLEDGED_ACCOUNT]: 'Pledged account',
        [IneligibleReasons.SUBJECT_TO_B_NOTICE]: 'TIN mismatch',
        [IneligibleReasons.NONRESIDENT_ALIEN]: 'Non-resident alien',
    };
    private readonly accountLevelIneligibleReasons = new Set<IneligibleReasons>([
        IneligibleReasons.ZERO_BALANCE,
        IneligibleReasons.ADDRESS_HOLD,
        IneligibleReasons.PLEDGED_ACCOUNT,
        IneligibleReasons.SUBJECT_TO_B_NOTICE,
        IneligibleReasons.NONRESIDENT_ALIEN,
    ]);
    private accountGroupsSubject = new BehaviorSubject<AccountGroup>({
        eligible: [],
        pendingRequests: [],
        requireForm: [],
        otherIneligible: [],
        temporarilyIneligible: [],
    });
    accountGroups$ = this.accountGroupsSubject.asObservable();
    private subs: Subscription = new Subscription();
    private scrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

    get allAccountsSelected(): boolean {
        return (
            this.eligibleAccountCount > 0 &&
            this.selectedAccountIds.size === this.eligibleAccountCount
        );
    }

    ngOnInit(): void {
        this.granteeName$ = this.store.select(selectAddUserStepperSelectedUserName);
        this.accessLevelLabel$ = this.store.select(selectAddUserStepperSelectedAccessLevelLabel);
        this.selectedAccess$ = this.store.select(selectAddUserStepperSelectedAccess);

        // Subscribe to account groups to track eligible accounts count
        this.subs.add(
            this.accountGroups$.subscribe((groups) => {
                this.eligibleAccountCount = groups.eligible.length;
            }),
        );

        // Subscribe to next step clicked
        this.subs.add(
            this.nextStepClicked?.subscribe(() => {
                this.onContinue();
            }),
        );

        this.subs.add(
            this.selectedAccess$.pipe(take(1)).subscribe((accessType) => {
                if (accessType === AccessTypeSelection.INFORMATION_ONLY) {
                    this.infoOnlyAccessPageName();
                    this.subs.add(this.nextStepClicked?.subscribe(() => this.onNextStepLoadInfoOnly()));
                } else {
                    this.limitedAccessPageName();
                    this.subs.add(this.nextStepClicked?.subscribe(() => this.onNextStepLoadLimited()));
                }
            }),
        );

        // Check pending requests for all accounts
        this.checkPendingRequests();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        if (this.scrollTimeoutId) {
            clearTimeout(this.scrollTimeoutId);
        }
    }

    private limitedAccessPageName(): void {}

    private infoOnlyAccessPageName(): void {}

    private onNextStepLoadLimited(): void {}

    private onNextStepLoadInfoOnly(): void {}

    onAccountChange(state: CheckboxState, account: OwnAccountDTO): void {
        if (!account.accountId) return;

        if (state.isChecked) {
            this.selectedAccountIds.add(account.accountId);
            this.showSelectionError = false;
        } else {
            this.selectedAccountIds.delete(account.accountId);
        }
    }

    toggleSelectAll(): void {
        if (this.allAccountsSelected) {
            this.store.select(selectUserInfo).pipe(take(1)).subscribe((userInfo) => {
                this.loggerService.info(`toggleSelectAll: deselecting all accounts (grantorPoid: ${userInfo?.clientPoid})`);
                this.selectedAccountIds.clear();
            });
        } else {
            combineLatest([this.accountGroups$, this.store.select(selectUserInfo)])
                .pipe(take(1))
                .subscribe(([groups, userInfo]) => {
                    groups.eligible.forEach((account) => {
                        if (account.accountId) {
                            this.selectedAccountIds.add(account.accountId);
                        }
                    });
                    this.loggerService.info(`toggleSelectAll: selecting all ${this.selectedAccountIds.size} eligible accounts (grantorPoid: ${userInfo?.clientPoid})`);
                    this.showSelectionError = false;
                });
        }
    }

    isAccountSelected(account: OwnAccountDTO): boolean {
        return !!account.accountId && this.selectedAccountIds.has(account.accountId);
    }

    getAccessLevelForPendingAccount(pendingInfo: AccountWithPendingInfo): string {
        return this.accessLevelLabels[pendingInfo?.accessLevel] ?? '';
    }

    getIneligibleReasonLabel(account: OwnAccountDTO): string {
        if (!account.isIneligible?.length) return '';
        const label = account.isIneligible
            .map((reason) => this.accountLevelIneligibleReasonLabels[reason] ?? '')
            .filter(Boolean)
            .join(', ');
        return label ? ` (${label})` : '';
    }

    onContinue(): void {
        if (this.selectedAccountIds.size === 0) {
            this.showSelectionError = true;
            this.scrollTimeoutId = setTimeout(() => {
                this.selectionErrorElement?.nativeElement?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 0);
            this.nextStepReady?.emit(false);
            return;
        }

        // Get selected accounts and dispatch to store
        combineLatest([this.accountGroups$, this.selectedAccess$])
            .pipe(take(1))
            .subscribe(([groups, selectedAccess]) => {
                const selectedAccounts = groups.eligible.filter((account) =>
                    account.accountId ? this.selectedAccountIds.has(account.accountId) : false,
                );

                const hasManagedAccount = selectedAccounts.some((account) =>
                    account.isIneligible?.includes(IneligibleReasons.MANAGED_ACCOUNT),
                );

                if (selectedAccess === AccessTypeSelection.LIMITED_ACCESS && hasManagedAccount) {
                    this.nextStepReady?.emit(false);
                    this.managedAccountModal.openModalDialog();
                    return;
                }

                const hasCashManagementAccount = selectedAccounts.some(
                    (account) => account.cashManagementAccount === true,
                );

                if (
                    selectedAccess === AccessTypeSelection.INFORMATION_ONLY &&
                    hasCashManagementAccount
                ) {
                    this.nextStepReady?.emit(false);
                    this.cashManagementModal.openModalDialog();
                    return;
                }

                this.store.dispatch(updateAddUserStepperSelectedAccounts({ selectedAccounts }));
                this.nextStepReady?.emit(true);
            });
    }

    onManagedAccountModalContinue(): void {
        this.accountGroups$.pipe(take(1)).subscribe((groups) => {
            const selectedAccounts = groups.eligible.filter((account) =>
                account.accountId ? this.selectedAccountIds.has(account.accountId) : false,
            );

            this.store.dispatch(updateAddUserStepperSelectedAccounts({ selectedAccounts }));
            this.nextStepReady?.emit(true);
        });
    }

    onManagedAccountModalBack(): void {
        this.nextStepReady?.emit(false);
    }

    onCashManagementModalContinue(): void {
        this.accountGroups$.pipe(take(1)).subscribe((groups) => {
            const selectedAccounts = groups.eligible.filter((account) =>
                account.accountId ? this.selectedAccountIds.has(account.accountId) : false,
            );

            this.store.dispatch(updateAddUserStepperSelectedAccounts({ selectedAccounts }));
            this.nextStepReady?.emit(true);
        });
    }

    onCashManagementModalBack(): void {
        this.nextStepReady?.emit(false);
    }

    onBack(): void {
        // Navigate back - let stepper handle this
        this.locationChanged?.emit(-1);
    }

    navigateToForm(): void {
        window.open(environment.AGENT_AUTHORIZATION_FORM_URL, LinkTarget.Self);
    }

    private checkPendingRequests(): void {
        combineLatest([
            this.store.select(selectOwnAccounts),
            this.store.select(selectAddUserStepperSelectedUser),
            this.store.select(selectAddUserStepperSelectedAccess),
        ])
            .pipe(take(1))
            .subscribe(([accounts, selectedUser, selectedAccess]) => {
                if (!accounts?.length || !selectedAccess) {
                    return;
                }

                const accountsWithId = accounts.filter((account) => account.accountId);
                const accountItems: MultiAccountItemDto[] = accountsWithId.map((account) => ({
                    accountId: account.accountId ?? '',
                    accountName: account.accountName ?? '',
                    serviceAgreementId: account.serviceAgreementId ?? '',
                    isVBA: account.isVBA ?? false,
                }));

                const dto: CheckPendingRequestsDto = {
                    accessLevel: selectedAccess,
                    granteePoid: selectedUser?.clientPoid,
                    granteeEmail: selectedUser?.email,
                    accounts: accountItems,
                };

                this.pendingRequestService.checkPendingRequestsMulti(dto).subscribe({
                    next: (response) => {
                        this.pendingRequestsResponse = response;
                        this.groupAccountsFromResponse(
                            accountsWithId,
                            response,
                            selectedUser?.clientPoid,
                            selectedAccess,
                        );
                        this.restorePreviouslySelectedAccounts();
                        this.isLoading = false;
                        if (this.eligibleAccountCount === 0) {
                            this.adobeLaunchService.subPageViewed({
                                subPageID: 'grant_access_select_accounts_no_eligible_accounts_error',
                                spClicked: true,
                            });
                        }
                    },
                    error: () => {
                        this.isLoading = false;
                        this.errorService.showError(ErrorPage.TECH_DIFF);
                    },
                });
            });
    }

    private groupAccountsFromResponse(
        accounts: OwnAccountDTO[],
        response: CheckPendingRequestsResponseDto,
        granteeClientPoid?: string,
        requestedAccess?: AccessTypeSelection,
    ): void {
        const groups: AccountGroup = {
            eligible: [],
            pendingRequests: response.accountsWithPending || [],
            requireForm: [],
            otherIneligible: [],
            temporarilyIneligible: [],
        };

        // Get IDs of accounts to process (eligible) and pending accounts
        const accountsToProcessIds = new Set(
            response.accountsToProcess?.map((a) => a.accountId) || [],
        );

        accounts.forEach((account) => {
            if (!account.accountId) return;

            const isFormRequired = this.accountRequiresForm(account.isIneligible ?? []);
            const isManagedAccountOnly = this.isManagedAccountOnly(account.isIneligible ?? []);
            const isTemporarilyIneligible = this.isTemporarilyIneligible(
                account.isIneligible ?? [],
            );

            if (isFormRequired) {
                groups.requireForm.push(account);
            } else if (isManagedAccountOnly) {
                // Managed accounts are eligible for LIMITED and INFO_ONLY (FULL_AUTHORITY redirects to form)
                groups.eligible.push(account);
            } else if (isTemporarilyIneligible) {
                groups.temporarilyIneligible.push(account);
            } else if (this.hasAccountLevelIneligibleReasons(account.isIneligible ?? [])) {
                groups.otherIneligible.push(account);
            } else if (accountsToProcessIds.has(account.accountId)) {
                if (
                    !this.granteeAlreadyHasSameAccess(account, granteeClientPoid, requestedAccess)
                ) {
                    groups.eligible.push(account);
                }
            }
        });

        this.accountGroupsSubject.next(groups);
    }

    private restorePreviouslySelectedAccounts(): void {
        combineLatest([
            this.store.select(selectAddUserStepperSelectedAccounts),
            this.accountGroups$,
        ])
            .pipe(take(1))
            .subscribe(([previouslySelected, groups]) => {
                if (!previouslySelected?.length) return;

                const eligibleIds = new Set(groups.eligible.map((a) => a.accountId));
                previouslySelected.forEach((account) => {
                    if (account.accountId && eligibleIds.has(account.accountId)) {
                        this.selectedAccountIds.add(account.accountId);
                    }
                });
            });
    }

    private isManagedAccountOnly(ineligibleReasons: IneligibleReasons[]): boolean {
        return (
            ineligibleReasons.length === 1 &&
            ineligibleReasons[0] === IneligibleReasons.MANAGED_ACCOUNT
        );
    }

    private accountRequiresForm(ineligibleReasons: IneligibleReasons[]): boolean {
        const formRequiringReasons = [IneligibleReasons.TRUST_OR_ORGANIZATION_ACCOUNT];
        return ineligibleReasons.some((reason) => formRequiringReasons.includes(reason));
    }

    private hasAccountLevelIneligibleReasons(ineligibleReasons: IneligibleReasons[]): boolean {
        return ineligibleReasons.some((reason) => this.accountLevelIneligibleReasons.has(reason));
    }

    private isTemporarilyIneligible(ineligibleReasons: IneligibleReasons[]): boolean {
        return ineligibleReasons.includes(IneligibleReasons.TECH_DIFF);
    }

    private granteeAlreadyHasSameAccess(
        account: OwnAccountDTO,
        granteeClientPoid?: string,
        requestedAccess?: AccessTypeSelection,
    ): boolean {
        if (!account.grantees?.length || !granteeClientPoid || !requestedAccess) return false;
        const existingAccess = accountPermissionUtil.getAccessLevel(account.grantees, granteeClientPoid);
        return existingAccess === requestedAccess;
    }
}
